import * as React from "react";
import {
  BookOpen,
  File,
  FileText,
  Github,
  HardDrive,
  Link2,
  Youtube,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../lib/cn";
import { Markdown } from "./Markdown";

/**
 * AIResponseBlock — persona-attributed AI answer card: persona header,
 * markdown body, optional source chips, and a confidence / latency / model
 * metadata row. The grounding-and-attribution shell for AI answers that need
 * to show their work (Explore answers, Intelligence summaries, studio Q&A).
 *
 * Ported from Creator Studio v2 (`src/components/ui/AIResponseBlock.tsx`)
 * and re-themed: the app-global `as-response-block` / `source-chip` CSS
 * classes and `font-ui` family are replaced with card + border tokens, and
 * the body renders through this library's `<Markdown>` (react-markdown +
 * sanitize) instead of the app's regex parser. The app-specific `mode` enum
 * ("explore" | "studio" | "intelligence") is generalised to `contextLabel`.
 *
 * For the pre-first-token state, pair with `<ThinkingDots>` or
 * `<TypingIndicator>` rather than rendering an empty block; once tokens
 * stream, pass `streaming` to show the caret.
 *
 * @example
 *   <AIResponseBlock
 *     persona={{ name: "Aiko", avatar: "A" }}
 *     contextLabel="Explore"
 *     content={answerMarkdown}
 *     sources={[{ type: "pdf", label: "onboarding-guide.pdf" }]}
 *     confidence={0.92}
 *     latency="1.2s"
 *     model="claude-opus-4-8"
 *   />
 */

const SOURCE_ICONS: Record<string, LucideIcon> = {
  pdf: FileText,
  url: Link2,
  youtube: Youtube,
  github: Github,
  gdrive: HardDrive,
  onedrive: HardDrive,
  notion: BookOpen,
};

const FALLBACK_SOURCE_ICON: LucideIcon = File;

export interface AISource {
  /** Source kind — "pdf" | "url" | "youtube" | "github" | "gdrive" | "onedrive" | "notion" pick an icon; unknown kinds get a generic file icon. */
  type: string;
  /** Display label for the chip. */
  label: string;
}

export interface AIResponseBlockProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "content"> {
  /** Persona shown in the header. `avatar` is usually 1-2 initials, but any node works. */
  persona: { name: string; avatar?: React.ReactNode };
  /** Markdown body, rendered through `<Markdown>`. */
  content: string;
  /** Optional source chips rendered under the body. */
  sources?: readonly AISource[];
  /** 0 to 1 confidence score, rendered as a percentage in the metadata row. */
  confidence?: number;
  /** Response latency copy, e.g. "1.2s". */
  latency?: string;
  /** Model name, e.g. "claude-opus-4-8". */
  model?: string;
  /** Right-aligned header label naming the surface ("Explore", "Intelligence", ...). */
  contextLabel?: React.ReactNode;
  /** Animate the block in on mount. Default true; collapses under reduced motion. */
  animate?: boolean;
  /** Forwarded to `<Markdown streaming>` — renders a trailing caret while tokens arrive. */
  streaming?: boolean;
  /** Heading above the source chips. Default "Sources". */
  sourcesLabel?: string;
}

const MetaDot = ({ label }: { label: React.ReactNode }) => (
  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
    <span className="inline-block size-1 rounded-full bg-border" aria-hidden />
    {label}
  </span>
);

export const AIResponseBlock = React.forwardRef<HTMLDivElement, AIResponseBlockProps>(
  (
    {
      persona,
      content,
      sources,
      confidence,
      latency,
      model,
      contextLabel,
      animate = true,
      streaming = false,
      sourcesLabel = "Sources",
      className,
      ...rest
    },
    ref,
  ) => {
    const hasMeta = confidence !== undefined || latency || model;
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-2xl border border-border/70 bg-card p-4 text-card-foreground sm:p-5",
          animate && "animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none",
          className,
        )}
        {...rest}
      >
        {/* Persona header */}
        <div className="mb-3.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex size-5 shrink-0 items-center justify-center rounded bg-primary text-[9px] font-semibold text-primary-foreground">
              {persona.avatar ?? persona.name.charAt(0).toUpperCase()}
            </span>
            <span className="text-xs font-medium text-foreground">{persona.name}</span>
          </div>
          {contextLabel ? (
            <span className="text-[10px] font-medium tracking-wide text-muted-foreground">
              {contextLabel}
            </span>
          ) : null}
        </div>

        <div className="mb-3.5 h-px bg-border" aria-hidden />

        {/* Body */}
        <Markdown content={content} streaming={streaming} className="text-sm" />

        {/* Sources */}
        {sources && sources.length > 0 ? (
          <div className="mt-4 border-t border-border/60 pt-3.5">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {sourcesLabel}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {sources.map((src, i) => {
                const Icon = SOURCE_ICONS[src.type.toLowerCase()] ?? FALLBACK_SOURCE_ICON;
                return (
                  <span
                    key={`${src.type}-${src.label}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground"
                  >
                    <Icon className="size-2.5 shrink-0" aria-hidden />
                    {src.label}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Metadata row */}
        {hasMeta ? (
          <div className="mt-3 flex items-center gap-3 border-t border-border/40 pt-2.5">
            {confidence !== undefined ? (
              <MetaDot label={`${Math.round(confidence * 100)}% confidence`} />
            ) : null}
            {latency ? <MetaDot label={latency} /> : null}
            {model ? <MetaDot label={model} /> : null}
          </div>
        ) : null}
      </div>
    );
  },
);
AIResponseBlock.displayName = "AIResponseBlock";
